import React, {useEffect, useState, useRef} from 'react'
import '../MerchantModal/MerchantModal.css'
import './ASPConfigModal.css'
import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
} from "@mui/material"
import {getASPConfig, postASPConfig, searchProducts, getProductsByIds, ProductPrimitive} from "../../apis/aspConfig"

interface ASPConfigModalProps {
  isOpen: boolean
  onClose: () => void
  shop: string
}

const ASPConfigModal: React.FC<ASPConfigModalProps> = ({isOpen, onClose, shop}) => {
  const [isRestricted, setIsRestricted] = useState(false)
  const [allowedProducts, setAllowedProducts] = useState<ProductPrimitive[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ProductPrimitive[]>([])
  const [searching, setSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const fetchConfig = async () => {
      try {
        setLoading(true)
        const response = await getASPConfig(shop)
        setIsRestricted(response.data.isRestricted)

        if (response.data.isRestricted && response.data.allowedProducts.length > 0) {
          try {
            const productsResponse = await getProductsByIds(shop, response.data.allowedProducts)
            setAllowedProducts(productsResponse.data)
          } catch (error) {
            console.error('Error fetching product details:', error)
            // Fall back to displaying raw IDs if product lookup fails
            setAllowedProducts(response.data.allowedProducts.map(id => ({
              id,
              externalId: '',
              title: id,
              description: '',
              pdpUrl: '',
              handle: '',
              featuredImage: null,
              price: 0,
              comparePrice: null,
              currencyCode: '',
              totalInventory: 0,
            })))
          }
        }
      } catch (error) {
        console.error('Error fetching ASP config:', error)
        alert('Error loading ASP configuration.')
      } finally {
        setLoading(false)
      }
    }

    fetchConfig()
  }, [isOpen, shop])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    setSearchQuery(query)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (query.trim().length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearching(true)
        const response = await searchProducts(shop, query)
        // Filter out already-added products
        const existingIds = new Set(allowedProducts.map(p => p.id))
        setSearchResults(response.data.filter(p => !existingIds.has(p.id)))
        setShowDropdown(true)
      } catch (error) {
        console.error('Error searching products:', error)
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  const handleAddProduct = (product: ProductPrimitive) => {
    setAllowedProducts(prev => [...prev, product])
    setSearchResults(prev => prev.filter(p => p.id !== product.id))
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveProduct = (productId: string) => {
    setAllowedProducts(prev => prev.filter(p => p.id !== productId))
  }

  const handleRestrictedChange = () => {
    const newRestricted = !isRestricted
    setIsRestricted(newRestricted)
    if (!newRestricted) {
      setAllowedProducts([])
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await postASPConfig(shop, {
        isRestricted,
        allowedProducts: allowedProducts.map(p => p.id),
      })
      onClose()
    } catch (error) {
      console.error('Error saving ASP config:', error)
      alert('Error saving ASP configuration. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-container">
      <div className="modal">
        <h2 className="modal-header">ASP Configuration</h2>
        <p className="asp-shop-label">{shop}</p>

        {loading ? (
          <div className="asp-loading">
            <CircularProgress size={40} />
          </div>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={isRestricted}
                  onChange={handleRestrictedChange}
                />
              }
              label="Restrict ASP to specific products"
              sx={{
                mb: 2,
                ml: '0px',
                borderRadius: '5px',
                width: '100%',
                outline: '1px solid lightgrey',
              }}
            />

            {isRestricted && (
              <div className="asp-product-section">
                <div className="asp-search-container" ref={dropdownRef}>
                  <TextField
                    label="Search products"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Type to search for products..."
                    InputProps={{
                      endAdornment: searching ? <CircularProgress size={20} /> : null,
                    }}
                  />
                  {showDropdown && searchResults.length > 0 && (
                    <div className="asp-search-dropdown">
                      {searchResults.map(product => (
                        <div
                          key={product.id}
                          className="asp-search-result"
                          onClick={() => handleAddProduct(product)}
                        >
                          {product.featuredImage && (
                            <img
                              src={product.featuredImage.smallUrl}
                              alt={product.title}
                              className="asp-product-thumb"
                            />
                          )}
                          <div className="asp-product-info">
                            <span className="asp-product-title">{product.title}</span>
                            <span className="asp-product-price">
                              {product.currencyCode} {product.price.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showDropdown && searchResults.length === 0 && !searching && searchQuery.trim().length >= 2 && (
                    <div className="asp-search-dropdown">
                      <div className="asp-search-no-results">No products found</div>
                    </div>
                  )}
                </div>

                {allowedProducts.length > 0 && (
                  <div className="asp-product-list">
                    <p className="asp-product-list-label">Allowed Products ({allowedProducts.length})</p>
                    {allowedProducts.map(product => (
                      <div key={product.id} className="asp-product-item">
                        {product.featuredImage && (
                          <img
                            src={product.featuredImage.smallUrl}
                            alt={product.title}
                            className="asp-product-thumb"
                          />
                        )}
                        <span className="asp-product-item-title">{product.title}</span>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProduct(product.id)}
                          className="asp-remove-btn"
                        >
                          ✕
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <Button
                className="create-merchant-button"
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button className="cancel-merchant-button" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ASPConfigModal
